import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchMandalBySlug, assetUrl } from '../services/api';
import logo from '../assets/logo.png';
import PhotoViewer from '../components/PhotoViewer';
import { linkifyText } from '../utils/linkify';
import './MandalDetail.css';

export default function MandalDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [mandal, setMandal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetchMandalBySlug(slug)
      .then((data) => { if (!cancelled) setMandal(data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
        else setNotFound(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="detail-not-found">
        <div className="container" style={{ textAlign: 'center', padding: '200px 0 100px' }}>
          <img src={logo} alt="Aarambh" style={{ height: '80px', objectFit: 'contain', marginBottom: '8px' }} />
          <h2>Loading mandal profile...</h2>
        </div>
      </div>
    );
  }

  if (notFound || !mandal) {
    return (
      <div className="detail-not-found">
        <div className="container" style={{ textAlign: 'center', padding: '200px 0 100px' }}>
          <img src={logo} alt="Aarambh" style={{ height: '80px', objectFit: 'contain', marginBottom: '8px' }} />
          <h2>Mandal Not Found</h2>
          <p>We couldn't find this mandal in our directory.</p>
          <Link to="/mandals" className="btn-primary" style={{ marginTop: '20px', display: 'inline-block' }}>
            ← Back to Directory
          </Link>
        </div>
      </div>
    );
  }

  const hasAbout = !!mandal.about;
  const yearsActive = new Date().getFullYear() - mandal.year;
  const upcomingEvents = mandal.events.filter((e) => e.isUpcoming);
  const pastEvents = mandal.events.filter((e) => !e.isUpcoming);

  return (
    <div className="mandal-detail-page">

      {/* Hero */}
      <div className="detail-hero">
        <div className="container">
          <button className="back-btn" onClick={() => navigate(-1)}>
            ← Back to Directory
          </button>
          <div className="detail-hero-content">
            <div className="detail-hero-icon">
              <img src={mandal.logoUrl ? assetUrl(mandal.logoUrl) : logo} alt={mandal.name} style={{ height: '90px', objectFit: 'contain' }} />
            </div>
            <div className="detail-hero-info">
              {mandal.verified && <span className="verified-badge-lg">✓ Verified Mandal</span>}
              <h1>{mandal.name}</h1>
              {mandal.tagline && <p className="detail-tagline">{mandal.tagline}</p>}
              <div className="detail-meta-row">
                <span>📍 {mandal.area}, Valsad</span>
                <span>📅 Est. {mandal.year}</span>
                <span>👥 {mandal.members}+ Members</span>
              </div>
              <div className="detail-activities">
                {mandal.activities.map((a) => (
                  <span key={a} className="activity-tag">{a}</span>
                ))}
              </div>
            </div>
            <div className="detail-hero-actions">
              <a href={`mailto:${mandal.email || 'info@aarambh.org'}`} className="btn-outline">✉️ Contact</a>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <section className="detail-stats-section">
        <div className="container">
          <div className="detail-stats-grid">
            <div className="detail-stat card">
              <span className="detail-stat-num">{mandal.members}+</span>
              <span className="detail-stat-label">Active Members</span>
            </div>
            <div className="detail-stat card">
              <span className="detail-stat-num">{yearsActive}+</span>
              <span className="detail-stat-label">Years Active</span>
            </div>
            <div className="detail-stat card">
              <span className="detail-stat-num">{mandal.activities.length}</span>
              <span className="detail-stat-label">Activity Types</span>
            </div>
            <div className="detail-stat card">
              <span className="detail-stat-num">{mandal.events.length}</span>
              <span className="detail-stat-label">Events Logged</span>
            </div>
          </div>
        </div>
      </section>

      {/* About + Contact */}
      <section className="detail-about-section">
        <div className="container detail-about-grid">
          <div className="detail-about-text">
            <div className="badge">About Us</div>
            <h2>Our Story</h2>
            {hasAbout ? (
              mandal.about.split('\n\n').map((para, i) => <p key={i}>{linkifyText(para)}</p>)
            ) : (
              <p>
                {mandal.name} is a registered Ganpati Mahotsav Mandal based in {mandal.area}, Valsad district.
                Established in {mandal.year}, the mandal has {mandal.members}+ active members and is dedicated to
                celebrating Ganesh Chaturthi and serving the community through various social activities.
              </p>
            )}
          </div>
          <div className="detail-contact-card card">
            <h3>Contact Information</h3>
            <ul className="detail-contact-list">
              {mandal.president && (
                <li>
                  <span className="contact-icon">👤</span>
                  <div>
                    <span className="contact-label">President</span>
                    <span className="contact-value">{mandal.president}</span>
                  </div>
                </li>
              )}
              {mandal.secretary && (
                <li>
                  <span className="contact-icon">📋</span>
                  <div>
                    <span className="contact-label">Secretary</span>
                    <span className="contact-value">{mandal.secretary}</span>
                  </div>
                </li>
              )}
              <li>
                <span className="contact-icon">📞</span>
                <div>
                  <span className="contact-label">Phone</span>
                  <span className="contact-value">{mandal.contact}</span>
                </div>
              </li>
              <li>
                <span className="contact-icon">✉️</span>
                <div>
                  <span className="contact-label">Email</span>
                  <span className="contact-value">{mandal.email}</span>
                </div>
              </li>
              {mandal.address && (
                <li>
                  <span className="contact-icon">📍</span>
                  <div>
                    <span className="contact-label">Address</span>
                    <span className="contact-value">{mandal.address}</span>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="detail-gallery-section">
        <div className="container">
          <div className="section-header">
            <div className="badge">Memories</div>
            <h2>Photo Gallery</h2>
            <div className="divider">
              <div className="divider-line" />
              <span className="divider-icon">🌸</span>
              <div className="divider-line right" />
            </div>
          </div>
          {mandal.gallery.length > 0 ? (
            <div className="gallery-grid">
              {mandal.gallery.map((img, i) => (
                <div
                  key={img.id}
                  className="gallery-item"
                  onClick={() => setViewer({ images: mandal.gallery.map((im) => ({ url: assetUrl(im.url), caption: im.caption })), index: i })}
                  style={{ cursor: 'pointer' }}
                >
                  <img src={assetUrl(img.url)} alt={img.caption || mandal.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {img.caption && <div className="gallery-overlay">{img.caption}</div>}
                </div>
              ))}
            </div>
          ) : (
            <p className="gallery-note">📸 Photos coming soon — contact the mandal to contribute photos.</p>
          )}
        </div>
      </section>

      {/* Events */}
      <section className="detail-events-section">
        <div className="container">
          <div className="section-header">
            <div className="badge">Events</div>
            <h2>Our Events</h2>
            <div className="divider">
              <div className="divider-line" />
              <span className="divider-icon">🌸</span>
              <div className="divider-line right" />
            </div>
          </div>
          {mandal.events.length > 0 ? (
            <div className="detail-events-list">
              {[...upcomingEvents, ...pastEvents].map((ev) => (
                <div key={ev.id} className="detail-event-row card">
                  <div className="detail-event-header">
                    <span className={`event-dot ${ev.isUpcoming ? 'upcoming' : 'past'}`} />
                    <div className="detail-event-info">
                      <h4>{ev.title}</h4>
                      <span>📅 {new Date(ev.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <span className={`detail-event-status ${ev.isUpcoming ? 'upcoming' : 'past'}`}>
                      {ev.isUpcoming ? '🟢 Upcoming' : '✅ Completed'}
                    </span>
                  </div>
                  {ev.description && (
                    <p className="detail-event-description">{linkifyText(ev.description)}</p>
                  )}
                  {ev.images && ev.images.length > 0 && (
                    <div className="detail-event-images">
                      {ev.images.map((img, i) => (
                        <div
                          key={img.id}
                          className="detail-event-image"
                          onClick={() => setViewer({ images: ev.images.map((im) => ({ url: assetUrl(im.url), caption: im.caption })), index: i })}
                          style={{ cursor: 'pointer' }}
                        >
                          <img src={assetUrl(img.url)} alt={img.caption || ev.title} />
                          {img.caption && <span className="detail-event-image-caption">{img.caption}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="gallery-note">📅 This mandal hasn't logged any events yet.</p>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="detail-bottom-cta">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2>Explore More Mandal</h2>
          <p>Discover other Ganpati Mahotsav Mandal celebrating and serving communities across {mandal.area} and beyond.</p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '24px', flexWrap: 'wrap' }}>
            <Link to="/mandals" className="btn-outline">← View All Mandal</Link>
          </div>
        </div>
      </section>

      {viewer && (
        <PhotoViewer
          images={viewer.images}
          index={viewer.index}
          onClose={() => setViewer(null)}
          onNavigate={(d) => setViewer((v) => ({ ...v, index: (v.index + d + v.images.length) % v.images.length }))}
        />
      )}

    </div>
  );
}
